import { useRow, useTimelineContext } from 'dnd-timeline';
import { type ReactNode, useRef, useState } from 'react';
import { copyId } from '../grouping.js';
import { type ScheduleDensity, densityClasses } from '../scheduleDensity.js';
import type { ScheduleCar } from '../types.js';
import { GAP_STYLE } from './barStyle.js';
import type { GapShade } from './bufferShading.js';
import { DRAG_THRESHOLD_PX } from './dropLogic.js';
import type { CarOccupancy } from './occupancy.js';

const UNAVAILABLE_WORD: Record<string, string> = {
  retired: 'Retired',
  inactive: 'Inactive',
  maintenance: 'In maintenance',
  unpublished: 'Not published',
  deleted: 'Deleted',
};

/**
 * 316 — one car: a sticky label on the left, then its subrows (overlapping periods stack instead of
 * covering each other).
 */
export function CarRow({
  car,
  children,
  droppable = false,
  occupancy,
  density = 'comfortable',
  range,
  suggestOutline,
  gaps,
  copyKey,
  alsoWith,
}: {
  car: ScheduleCar;
  children: ReactNode;
  /** 316: a booking may be dropped here (the caller may move bookings). */
  droppable?: boolean;
  /** 317 (US1): the car's booked share of the view, beside its plate. */
  occupancy?: CarOccupancy;
  /** 318: Compact puts the car column on one line and shortens the row. */
  density?: ScheduleDensity;
  /** 328: the view's range, to place the suggestion outline. */
  range?: { start: number; end: number };
  /** 328 (#736, FR-006): the estimated suggested free time on THIS row while a drag clashes. */
  suggestOutline?: { start: number; end: number };
  /** 386 (#1278, FR-012): the car's gap after each held rental, drawn behind the bars. */
  gaps?: GapShade[];
  /** 396 (#1342): grouped by host, the group this copy of the car is drawn in; its row id carries it. */
  copyKey?: string;
  /** 396: the car's other hosts, named under this copy ("also with …"). */
  alsoWith?: string[];
}) {
  const compact = density === 'compact';
  const rowId = copyId(car.id, copyKey);
  const { setNodeRef, rowWrapperStyle, rowStyle, rowSidebarStyle, isOver } = useRow({
    id: rowId,
    disabled: !droppable,
  });
  return (
    <div
      data-testid={`schedule-row-${rowId}`}
      style={rowWrapperStyle}
      data-over={isOver ? 'true' : undefined}
      {...(car.unavailable ? { 'data-unavailable': car.unavailable.reason } : {})}
      // FR-005: the target row is marked as the target only — never as free or taken.
      className={[
        // 141 FR-008: rows are not divided by a border; the tonal hover DataTable uses instead.
        densityClasses(density).row,
        'hover:bg-surface motion-safe:transition-colors',
        isOver ? 'bg-surface-feature' : '',
      ].join(' ')}
    >
      <div
        style={rowSidebarStyle}
        data-testid={`schedule-car-${rowId}`}
        data-lines={compact ? '1' : '2'}
        // One line can truncate the plate; hovering the column shows it in full.
        title={compact ? `${car.label} · ${car.plate_number}` : undefined}
        className={[
          'sticky border-r border-hairline bg-canvas px-2',
          // 318 (R3): one line in Compact — name, plate, figure — so the row can be 28 px.
          compact ? 'items-center gap-1.5' : 'flex-col justify-center py-1',
        ].join(' ')}
      >
        <span
          className={[
            'truncate font-medium text-ink',
            compact ? 'min-w-0 shrink text-xs' : 'text-sm',
          ].join(' ')}
        >
          {car.label}
        </span>
        <span
          className={[
            'flex min-w-0 items-baseline gap-1.5 text-steel',
            compact ? 'flex-1 text-[11px]' : 'text-xs',
          ].join(' ')}
        >
          <span className="min-w-0 truncate">
            {car.plate_number}
            {car.unavailable
              ? ` · ${UNAVAILABLE_WORD[car.unavailable.reason] ?? 'Unavailable'}`
              : ''}
          </span>
          {/* 317 (FR-001, FR-006): the figure, in words; "Unavailable" / "Blocked" never "0%". */}
          {occupancy && (
            <span
              data-testid={`schedule-occupancy-${car.id}`}
              data-occupancy-kind={occupancy.kind}
              className="ml-auto shrink-0 text-[11px] font-medium text-slate"
            >
              {occupancy.label}
            </span>
          )}
        </span>
        {/* 396 (mock-up screen 2): the car is also drawn under its other hosts. */}
        {alsoWith && alsoWith.length > 0 && (
          <span
            data-testid="schedule-also-with"
            title={`also with ${alsoWith.join(', ')}`}
            className={[
              'max-w-full self-start truncate rounded-full bg-brand-blue-soft px-1.5 text-[11px] font-medium text-brand-blue-dark',
              compact ? 'shrink-0' : 'mt-0.5',
            ].join(' ')}
          >
            {`also with ${alsoWith.join(', ')}`}
          </span>
        )}
      </div>
      {/* Bars that run past the view's edge are clipped here, per lane (see ScheduleTimeline). */}
      <div ref={setNodeRef} style={rowStyle} className="overflow-hidden">
        {/* 301: a car unavailable for a reason held on itself is banded across the whole lane, so
            an empty row is never read as free. Behind the bars, and never a drop hint. */}
        {car.unavailable && (
          <div
            data-testid={`schedule-unavailable-${car.id}`}
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex items-center justify-center bg-[repeating-linear-gradient(45deg,var(--color-surface-soft)_0,var(--color-surface-soft)_8px,var(--color-surface)_8px,var(--color-surface)_16px)] text-xs font-medium text-slate"
          >
            {UNAVAILABLE_WORD[car.unavailable.reason] ?? 'Unavailable'}
          </div>
        )}
        {range && gaps?.map((g) => <GapShadeBlock key={g.key} shade={g} range={range} />)}
        {children}
        {suggestOutline && range && <SuggestOutline outline={suggestOutline} range={range} />}
      </div>
    </div>
  );
}

/** 386: the gap after a rental, behind the bars; never a bar, a drop target or anything to click. */
function GapShadeBlock({
  shade,
  range,
}: {
  shade: GapShade;
  range: { start: number; end: number };
}) {
  const { valueToPixels } = useTimelineContext();
  if (shade.end <= range.start || shade.start >= range.end) return null;
  return (
    <div
      data-testid={`schedule-gap-${shade.key}`}
      data-start={new Date(shade.start).toISOString()}
      data-end={new Date(shade.end).toISOString()}
      aria-hidden="true"
      className={['pointer-events-none absolute inset-y-1 rounded-r opacity-80', GAP_STYLE].join(
        ' ',
      )}
      style={{
        left: valueToPixels(shade.start - range.start),
        width: valueToPixels(shade.end - shade.start),
      }}
    />
  );
}

/** 328: a faint dashed box where the estimated suggestion would be; never a drop target. */
function SuggestOutline({
  outline,
  range,
}: {
  outline: { start: number; end: number };
  range: { start: number; end: number };
}) {
  const { valueToPixels } = useTimelineContext();
  return (
    <div
      data-testid="schedule-suggest-outline"
      data-start={new Date(outline.start).toISOString()}
      data-end={new Date(outline.end).toISOString()}
      aria-hidden="true"
      className="pointer-events-none absolute inset-y-1 rounded border-2 border-dashed border-brand-blue/60 bg-brand-blue/5"
      style={{
        left: valueToPixels(outline.start - range.start),
        width: valueToPixels(outline.end - outline.start),
      }}
    />
  );
}

/**
 * One stacking lane inside a car's row. With `onDraw`, a press on its EMPTY part followed by a
 * sideways movement of at least 4 px draws a new block (a press on a bar is that bar's own drag).
 */
export function Subrow({
  children,
  onDraw,
  density = 'comfortable',
}: {
  children: ReactNode;
  onDraw?: (fromMs: number, toMs: number, anchorRect: DOMRect) => void;
  /** 318: the lane's height. */
  density?: ScheduleDensity;
}) {
  const { getValueFromScreenX, getDeltaXFromScreenX } = useTimelineContext();
  const [ghost, setGhost] = useState<{ from: number; to: number } | null>(null);
  const laneRef = useRef<HTMLDivElement>(null);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!onDraw || e.button !== 0 || e.target !== e.currentTarget) return;
    const startX = e.clientX;
    const move = (ev: PointerEvent) => {
      if (Math.abs(ev.clientX - startX) < DRAG_THRESHOLD_PX) return;
      setGhost({ from: getDeltaXFromScreenX(startX), to: getDeltaXFromScreenX(ev.clientX) });
    };
    const up = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setGhost(null);
      if (Math.abs(ev.clientX - startX) < DRAG_THRESHOLD_PX) return;
      const lane = laneRef.current?.getBoundingClientRect() ?? new DOMRect();
      const left = Math.min(startX, ev.clientX);
      onDraw(
        getValueFromScreenX(startX),
        getValueFromScreenX(ev.clientX),
        new DOMRect(left, lane.top, Math.abs(ev.clientX - startX), lane.height),
      );
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    e.preventDefault(); // FR-002: no text selection while drawing
  };

  return (
    <div
      ref={laneRef}
      data-testid="schedule-subrow"
      className={['relative', densityClasses(density).lane, onDraw ? 'cursor-crosshair' : ''].join(
        ' ',
      )}
      onPointerDown={onDraw ? onPointerDown : undefined}
    >
      {children}
      {ghost && (
        <div
          data-testid="schedule-draw-ghost"
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-1 rounded border-2 border-dashed border-brand-blue bg-brand-blue-soft/60"
          style={{ left: Math.min(ghost.from, ghost.to), width: Math.abs(ghost.to - ghost.from) }}
        />
      )}
    </div>
  );
}
